import { useState } from "react";

import { FormField, inputStyle } from "../ui/FormField";
import type { ConsentAttestation } from "../../api/engagements";
import { sha256HexBytes } from "../../lib/videoDrill";

// ─── StageBadge ─────────────────────────────────────────────────────────────
// Defined here and exported so VideoConfigCard + VideoConsentCard can import it
// without duplicating the definition.

export type StageTone = "muted" | "warning" | "success";

interface BadgeProps {
  label: string;
  tone: StageTone;
}

const TONE_COLORS: Record<StageTone, { bg: string; text: string; border: string }> = {
  muted: {
    bg: "rgba(107,98,86,0.10)",
    text: "var(--text-muted)",
    border: "rgba(107,98,86,0.20)",
  },
  warning: {
    bg: "rgba(180,83,9,0.10)",
    text: "var(--warning)",
    border: "rgba(180,83,9,0.25)",
  },
  success: {
    bg: "rgba(21,128,61,0.10)",
    text: "var(--success)",
    border: "rgba(21,128,61,0.25)",
  },
};

export function StageBadge({ label, tone }: BadgeProps) {
  const colors = TONE_COLORS[tone];
  return (
    <span
      style={{
        display: "inline-block",
        background: colors.bg,
        color: colors.text,
        border: `1px solid ${colors.border}`,
        borderRadius: 4,
        padding: "2px 7px",
        fontFamily: "var(--font-mono)",
        fontSize: 9,
        fontWeight: 600,
        letterSpacing: "0.14em",
        textTransform: "uppercase",
        whiteSpace: "nowrap",
      }}
    >
      {label}
    </span>
  );
}

// ─── ConsentAttestationField ─────────────────────────────────────────────────

interface Props {
  label: string;
  value: ConsentAttestation | null;
  onChange: (next: ConsentAttestation | null) => void;
}

export function ConsentAttestationField({ label, value, onChange }: Props) {
  const sha = value?.sha256 ?? "";
  const signedBy = value?.signed_by ?? "";
  const signedAt = value?.signed_at ?? "";

  const [hashing, setHashing] = useState(false);

  function patch(next: Partial<ConsentAttestation>) {
    onChange({
      sha256: sha,
      signed_by: signedBy,
      signed_at: signedAt,
      ...next,
    });
  }

  async function onFile(file: File | undefined) {
    if (!file) return;
    setHashing(true);
    try {
      const buf = await file.arrayBuffer();
      patch({ sha256: await sha256HexBytes(buf) });
    } finally {
      setHashing(false);
    }
  }

  // Completeness: all three fields filled
  const complete = !!(sha && signedBy && signedAt);

  return (
    <FormField label={label}>
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {/* File input */}
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <input
            type="file"
            accept=".pdf,application/pdf"
            onChange={(e) => void onFile(e.target.files?.[0] ?? undefined)}
            style={{
              fontSize: 12,
              fontFamily: "var(--font-sans)",
              color: "var(--text-secondary)",
              cursor: "pointer",
              flex: 1,
              minWidth: 0,
            }}
          />
          {/* Completeness dot */}
          <span
            title={complete ? "Complete" : "Incomplete"}
            style={{
              width: 8,
              height: 8,
              borderRadius: "50%",
              background: complete ? "var(--success)" : "var(--text-muted)",
              flexShrink: 0,
              display: "inline-block",
            }}
          />
        </div>

        {/* Helper text */}
        <span
          style={{
            fontSize: 10.5,
            fontFamily: "var(--font-mono)",
            color: "var(--text-muted)",
            letterSpacing: "0.02em",
            lineHeight: 1.5,
          }}
        >
          {hashing
            ? "Hashing…"
            : sha
              ? `sha256: ${sha.slice(0, 12)}… · computed locally, file not uploaded`
              : "Drop the signed PDF — hashed in your browser"}
        </span>

        {/* Manual SHA-256 paste */}
        <input
          style={inputStyle}
          placeholder="…or paste the sha256 (64 hex)"
          value={sha}
          onChange={(e) => patch({ sha256: e.target.value })}
        />

        {/* Signed by + signed at row */}
        <div style={{ display: "flex", gap: 8 }}>
          <input
            style={{ ...inputStyle, flex: 1, minWidth: 0 }}
            placeholder="signed by"
            value={signedBy}
            onChange={(e) => patch({ signed_by: e.target.value })}
          />
          <input
            type="date"
            style={{ ...inputStyle, flex: "0 0 auto", width: 160 }}
            value={signedAt ? signedAt.slice(0, 10) : ""}
            onChange={(e) =>
              patch({
                signed_at: e.target.value
                  ? new Date(e.target.value).toISOString()
                  : "",
              })
            }
          />
        </div>
      </div>
    </FormField>
  );
}
